import { Button, Colors, TextFieldWrapper } from "@factorearth/component-library";
import pixelWidth from "string-pixel-width";
import React, { useState } from "react";
import styles from "./MeasurePixels.module.scss";
import { FiSliders, FiChevronDown, FiChevronUp } from "react-icons/fi";

interface MeasurePixelsProps {
	values: {
		value: string;
		fontSize?: number;
		font?: string;
		bold?: boolean;
	};
	valuesKey: string;
	handleChange: (event: { target: { name: string; value: any } }) => void;
	color: Colors;
	fromTable?: boolean;
}

type FontType =
	| "andale mono"
	| "arial"
	| "avenir"
	| "avenir next"
	| "comic sans ms"
	| "courier new"
	| "georgia"
	| "helvetica"
	| "impact"
	| "times new roman"
	| "trebuchet ms"
	| "verdana"
	| "webdings"
	| "open sans"
	| "tahoma";

function MeasurePixels(props: MeasurePixelsProps) {
	const { values, valuesKey, handleChange, color, fromTable } = props;
	// Add these state variables after the existing useState declarations (around line 142):
	const [pixelMeasureExpanded, setPixelMeasureExpanded] = useState<boolean>(false);
	const [pixelMeasureValues, setPixelMeasureValues] = useState<string>(values.value || "");
	const [valueToMeasure, setValueToMeasure] = useState<string>("");

	function getPixelLength(string: string, size: string, font: FontType, bold: boolean): number {
		const newSize = parseInt(size, 10);
		const width = Math.ceil(pixelWidth(string, { font: font, size: newSize, bold }));
		return width;
	}

	// Replace the existing handlePixelMeasureInput function (around line 962):
	function handlePixelMeasureInput() {
		const isExpanded = pixelMeasureExpanded || false;

		const handleToggleExpand = () => {
			setPixelMeasureExpanded(!isExpanded);
		};

		const handleMeasureChange = (field: string, newValue: string) => {
			// Recalculate pixels when any measurement parameter changes
			const measuredPixels = getPixelLength(
				newValue,
				values.fontSize ? values.fontSize.toString() : "12",
				(values.font as FontType) || "arial",
				values.bold ?? false,
			);

			setValueToMeasure(newValue);
			setPixelMeasureValues(measuredPixels.toString());
			handleChange({ target: { name: valuesKey, value: measuredPixels } });
		};

		const html = (
			<div className={styles.row} data-test="field-prop-list-item" key={`pixel-measure-${valuesKey}`}>
				{/* Main input field */}
				<div className={styles.inputWrapper}>
					<label style={{ color: color.text.primary }} className={styles.header} htmlFor={valuesKey}>
						{valuesKey === "pixelInitial" ? "Pixel Initial" : "Pixel Following"}:
					</label>
					<TextFieldWrapper
						id={fromTable ? `${valuesKey}-cell` : valuesKey}
						type="number"
						value={values.value}
						name={valuesKey}
						onChange={handleChange}
						placeholder={`Enter pixel value for ${valuesKey} or expand to measure content.`}
						colorPalette={color}
						style={{
							backgroundColor: color.background.primary,
							border: `${color.border.primary} 1px solid`,
							color: color.text.primary,
						}}
					/>
				</div>

				{/* Pixel Measurement Toggle */}
				<div
					onClick={handleToggleExpand}
					className={styles.advanced_button_container}
					style={{
						backgroundColor: color.background.secondary,
						border: `${color.border.primary} 1px solid`,
						borderBottom: isExpanded ? "none" : `${color.border.primary} 1px solid`,
						paddingBottom: isExpanded ? "0px" : undefined,
						color: color.text.tertiary,
						padding: "10px",
						marginTop: "8px",
					}}>
					<div>Pixel Measurement Tool</div>
					{isExpanded ? <FiChevronUp /> : <FiChevronDown />}
				</div>

				{/* Pixel Measurement Form */}
				{isExpanded && (
					<div
						style={{
							backgroundColor: color.background.secondary,
							color: color.text.primary,
							padding: "16px",
							border: `${color.border.primary} 1px solid`,
							borderTop: "none",
						}}>
						{/* Font Size Input */}
						<div className={styles.inputWrapper} style={{ marginBottom: "12px" }}>
							<label style={{ color: color.text.primary }}>Font Size: {values.fontSize}</label>
						</div>

						{/* Font Family Dropdown */}
						<div className={styles.inputWrapper} style={{ marginBottom: "12px" }}>
							<label style={{ color: color.text.primary }}>Font: {values.font}</label>
						</div>

						{/* Bold Checkbox */}
						<div className={styles.inputWrapper} style={{ marginBottom: "12px" }}>
							<label style={{ color: color.text.primary, display: "flex", alignItems: "center", gap: "8px" }}>
								Bold Text {values.bold ? "Yes" : "No"}
							</label>
						</div>

						{/* Test Text Input */}
						<div className={styles.inputWrapper} style={{ marginBottom: "12px" }}>
							<label style={{ color: color.text.primary }}>Test Text:</label>
							<TextFieldWrapper
								type="text"
								value={valueToMeasure}
								onChange={(e) => handleMeasureChange("value", e.target.value)}
								placeholder="Type text to measure pixel width..."
								colorPalette={color}
								style={{
									backgroundColor: color.background.primary,
									border: `${color.border.primary} 1px solid`,
									color: color.text.primary,
								}}
							/>
						</div>

						{/* Measured Pixels Display */}
						<div className={styles.inputWrapper} style={{ marginBottom: "12px" }}>
							<label style={{ color: color.text.primary }}>Measured Pixels:</label>
							<div
								style={{
									backgroundColor: color.background.tertiary,
									border: `${color.border.primary} 1px solid`,
									color: color.text.primary,
									padding: "8px",
									borderRadius: "4px",
									fontWeight: "bold",
								}}>
								{values.value} px
							</div>
						</div>

						{/* Action Buttons */}
						<div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
							<Button
								colorPalette={color}
								variant="destructive"
								style={{ background: color.background.tertiary }}
								onClick={() => handleMeasureChange("value", "")}>
								Clear
							</Button>
						</div>
					</div>
				)}
			</div>
		);

		return html;
	}

	return handlePixelMeasureInput();
}

export default MeasurePixels;
